import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useApi } from '@gravitate-js/the-armory'
import { getAPIURL } from '../../utils/api'

export function useMatchCommentActions() {
  const queryClient = useQueryClient()
  const api = useApi({ baseURLOverride: getAPIURL() })

  const invalidateMatchQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ['post', '/supplier_rec/match/overview'],
    })
    queryClient.invalidateQueries({
      queryKey: ['matchOverview'],
    })
  }

  const useAddCommentMutation = () => {
    return useMutation({
      mutationFn: async (data: { match_id: string; msg: string }) => {
        const response = await api.post('supplier_rec/match/comment', data)
        return response
      },
      onMutate: async (request) => {
        await queryClient.cancelQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        const previousData = queryClient.getQueriesData({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        const currentUser = { username: 'current_user' }

        queryClient.setQueriesData(
          { queryKey: ['post', '/supplier_rec/match/detail'] },
          (oldData: { comments?: unknown[]; [key: string]: unknown } | undefined) => {
            if (!oldData) return oldData

            const newComment = {
              id: `temp_${Date.now()}`,
              text: request.msg,
              username: currentUser.username,
              created_on: new Date().toISOString(),
            }

            return {
              ...oldData,
              comments: [...(oldData.comments || []), newComment],
            }
          }
        )

        return { previousData }
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })
        invalidateMatchQueries()
      },
      onError: (error, request, context) => {
        if (context?.previousData && context.previousData.length > 0) {
          context.previousData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data)
          })
        }
      },
    })
  }

  const useRemoveCommentMutation = () => {
    return useMutation({
      mutationFn: async (data: { match_id: string; comment_id: string }) => {
        const response = await api.post('supplier_rec/match/remove_comment', data)
        return response
      },
      onMutate: async (request) => {
        await queryClient.cancelQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        const previousData = queryClient.getQueriesData({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        queryClient.setQueriesData(
          { queryKey: ['post', '/supplier_rec/match/detail'] },
          (oldData: { comments?: unknown[]; [key: string]: unknown } | undefined) => {
            if (!oldData || !oldData.comments) return oldData

            return {
              ...oldData,
              comments: (oldData.comments as Array<{ id: string }>)?.filter(
                (comment) => comment.id !== request.comment_id
              ),
            }
          }
        )

        return { previousData }
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })
        invalidateMatchQueries()
      },
      onError: (error, request, context) => {
        if (context?.previousData && context.previousData.length > 0) {
          context.previousData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data)
          })
        }
      },
    })
  }

  return {
    useAddCommentMutation,
    useRemoveCommentMutation,
    invalidateMatchQueries,
  }
}
